import { lazy, Suspense, type ReactNode } from "react"
import { Navigate, Route, Routes, useParams } from "react-router-dom"
import { AuthenticatedAppRoute } from "@/components/auth/AuthenticatedAppRoute"
import { LoadingState } from "@/components/ui/loading-state"
import { useIntercom } from "@/hooks/useIntercom"

const AppHome = lazy(() => import("@/pages/AppHome"))
const ClientChats = lazy(() => import("@/pages/ClientChats"))
const ClientChatSession = lazy(() => import("@/pages/ClientChatSession"))
const ClientSkills = lazy(() => import("@/pages/ClientSkills"))
const ClientTasks = lazy(() => import("@/pages/ClientTasks"))
const ClientTransactions = lazy(() => import("@/pages/ClientTransactions"))
const ClientWorkspace = lazy(() => import("@/pages/ClientWorkspace"))
const DocumentViewerHarness = lazy(() => import("@/pages/DocumentViewerHarness"))
const Home = lazy(() => import("@/pages/Home"))
const IntegrationDetail = lazy(() => import("@/pages/IntegrationDetail"))
const Integrations = lazy(() => import("@/pages/Integrations"))
const Privacy = lazy(() => import("@/pages/Privacy"))
const Research = lazy(() => import("@/pages/Research"))
const ResearchPost = lazy(() => import("@/pages/ResearchPost"))
const Support = lazy(() => import("@/pages/Support"))
const Terms = lazy(() => import("@/pages/Terms"))
const WorkOSOrganizationSelection = lazy(() => import("@/pages/WorkOSOrganizationSelection"))

export default function App() {
  useIntercom()

  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/app" element={<AppHome />} />
        <Route
          path="/app/clients/:clientId"
          element={(
            <AuthenticatedAppRoute>
              <ClientWorkspace />
            </AuthenticatedAppRoute>
          )}
        >
          <Route index element={<ClientWorkspaceIndexRedirect />} />
          <Route
            path="tasks"
            element={(
              <WorkspaceOutletSuspense>
                <ClientTasks />
              </WorkspaceOutletSuspense>
            )}
          />
          <Route
            path="chats"
            element={(
              <WorkspaceOutletSuspense>
                <ClientChats />
              </WorkspaceOutletSuspense>
            )}
          />
          <Route path="chats/new" element={<ClientNewChatRedirect />} />
          <Route
            path="chats/:chatId"
            element={(
              <WorkspaceOutletSuspense>
                <ClientChatSession />
              </WorkspaceOutletSuspense>
            )}
          />
          <Route
            path="transactions"
            element={(
              <WorkspaceOutletSuspense>
                <ClientTransactions />
              </WorkspaceOutletSuspense>
            )}
          />
          <Route
            path="skills"
            element={(
              <WorkspaceOutletSuspense>
                <ClientSkills />
              </WorkspaceOutletSuspense>
            )}
          />
        </Route>
        <Route path="/auth/select-organisation" element={<WorkOSOrganizationSelection />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/integrations/:slug" element={<IntegrationDetail />} />
        <Route path="/research" element={<Research />} />
        <Route path="/research/:slug" element={<ResearchPost />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/support" element={<Support />} />
        <Route path="/terms" element={<Terms />} />
        {import.meta.env.DEV ? (
          <Route path="/dev/document-viewer" element={<DocumentViewerHarness />} />
        ) : null}
        <Route path="*" element={<Navigate replace to="/" />} />
      </Routes>
    </Suspense>
  )
}

function RouteLoadingFallback() {
  return (
    <main className="flex min-h-svh items-center justify-center bg-background px-6 text-foreground">
      <LoadingState ariaLabel="Loading page" />
    </main>
  )
}

function WorkspaceOutletSuspense({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<WorkspaceOutletLoadingFallback />}>
      {children}
    </Suspense>
  )
}

function WorkspaceOutletLoadingFallback() {
  return (
    <main className="min-h-0 flex-1 overflow-auto px-4 pb-10 pt-10 md:px-6">
      <div className="mx-auto flex w-full max-w-190 items-center justify-center py-20">
        <LoadingState ariaLabel="Loading workspace page" />
      </div>
    </main>
  )
}

function ClientWorkspaceIndexRedirect() {
  const { clientId } = useParams()

  return <Navigate replace to={`/app/clients/${clientId}/tasks`} />
}

function ClientNewChatRedirect() {
  const { clientId } = useParams()

  return <Navigate replace to={`/app/clients/${clientId}/chats?new=1`} />
}
